import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Tooltip,
  Popover,
  Stack,
  TableSortLabel,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Analytics as AnalyticsIcon,
  Info as InfoIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  TrendingFlat as TrendingFlatIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

interface FinancialDataOverviewProps {
  financialData: any;
  dataQuality?: {
    score: number;
    level: 'high' | 'medium' | 'low';
    missing: string[];
    available: string[];
  };
  yearRange: { start: number; end: number };
  onYearRangeChange: (range: { start: number; end: number }) => void;
}

interface TableColumn {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
}

const FinancialDataOverview: React.FC<FinancialDataOverviewProps> = ({
  financialData,
  dataQuality,
  yearRange,
  onYearRangeChange
}) => {
  const theme = useTheme();

  // Helper function to calculate YoY change
  const calculateYoYChange = (current: number | undefined, previous: number | undefined): number | null => {
    if (!current || !previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Helper function to calculate simple trend direction
  const calculateTrend = (values: number[]): 'up' | 'down' | 'flat' => {
    const validValues = values.filter(v => v > 0);
    if (validValues.length < 2) return 'flat';

    const first = validValues[validValues.length - 1]; // oldest
    const last = validValues[0]; // newest

    if (last > first * 1.05) return 'up'; // 5% threshold
    if (last < first * 0.95) return 'down';
    return 'flat';
  };

  // State for table functionality
  const [sortBy, setSortBy] = useState<string>('year');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dataQualityAnchorEl, setDataQualityAnchorEl] = useState<HTMLElement | null>(null);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'year', label: 'Year', visible: true, sortable: true },
    { id: 'revenue', label: 'Revenue', visible: true, sortable: true },
    { id: 'revenueChange', label: 'Revenue YoY', visible: true, sortable: true },
    { id: 'expenses', label: 'Expenses', visible: true, sortable: true },
    { id: 'expenseChange', label: 'Expense YoY', visible: true, sortable: true },
    { id: 'noi', label: 'NOI', visible: true, sortable: true },
    { id: 'noiChange', label: 'NOI YoY', visible: true, sortable: true },
    { id: 'occupancy', label: 'Occupancy', visible: true, sortable: true },
    { id: 'occupancyChange', label: 'Occupancy YoY', visible: true, sortable: true },
    { id: 'adr', label: 'ADR', visible: true, sortable: true },
    { id: 'adrChange', label: 'ADR YoY', visible: true, sortable: true },
    { id: 'revpar', label: 'RevPAR', visible: true, sortable: true },
    { id: 'revparChange', label: 'RevPAR YoY', visible: true, sortable: true }
  ]);

  // Process the raw financial data into structured format
  const processedFinancialData = useMemo(() => {
    if (!financialData) return null;

    const confirmedFinancials = financialData.confirmedFinancials || [];
    const hotelMetrics = financialData.hotelMetrics || [];
    const profitLoss = financialData.profitLoss || [];

    // Debug: Track what we're processing
    console.log('FinancialDataOverview - Starting data processing:', {
      confirmedFinancials: confirmedFinancials.length,
      profitLoss: profitLoss.length,
      hotelMetrics: hotelMetrics.length
    });

    // Debug: Check for potential overlap in the raw data
    if (confirmedFinancials.length > 0 && profitLoss.length > 0) {
      console.log('FinancialDataOverview - Checking for data overlap...');

      // Sample the data sources
      console.log('Sample confirmed financials:', confirmedFinancials.slice(0, 3));
      console.log('Sample profit & loss:', profitLoss.slice(0, 3));

      // Check if profit & loss contains "confirmed" source items
      const confirmedSourceItems = profitLoss.filter((item: any) => item.source === 'confirmed');
      if (confirmedSourceItems.length > 0) {
        console.warn('⚠️ Profit & Loss data contains confirmed source items:', confirmedSourceItems.length);
      }
    }

    // If we have confirmed financials, prioritize them and be very selective about P&L data
    const hasConfirmedFinancials = confirmedFinancials.length > 0;

    console.log(`FinancialDataOverview - Has confirmed financials: ${hasConfirmedFinancials}`);

    // Helper function to safely parse numbers
    const safeParseNumber = (value: any): number => {
      if (value === null || value === undefined || value === '') return 0;

      // Handle boolean values
      if (typeof value === 'boolean') return value ? 1 : 0;

      // Handle objects (return 0 for any object)
      if (typeof value === 'object') return 0;

      const parsed = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    // Helper function to categorize financial items
    const categorizeFinancialItem = (category: string): 'revenue' | 'expense' | 'other' => {
      if (!category || typeof category !== 'string') {
        console.warn('FinancialDataOverview - Invalid category for categorization:', category);
        return 'other';
      }

      const cat = category.toLowerCase();

      // Revenue indicators
      const revenueKeywords = [
        'revenue', 'income', 'sales', 'rooms revenue', 'room revenue',
        'food & beverage', 'f&b revenue', 'other revenue', 'rental income',
        'service revenue', 'ancillary revenue'
      ];

      // Expense indicators
      const expenseKeywords = [
        'expense', 'cost', 'wages', 'salaries', 'payroll', 'utilities',
        'insurance', 'maintenance', 'repairs', 'supplies', 'marketing',
        'advertising', 'administrative', 'general', 'management', 'fees',
        'taxes', 'interest', 'depreciation', 'amortization', 'cleaning',
        'housekeeping', 'laundry', 'security', 'telephone', 'internet',
        'cable', 'tv', 'energy', 'water', 'sewer', 'gas', 'electric',
        'heating', 'cooling', 'travel', 'transportation', 'professional',
        'legal', 'accounting', 'audit', 'consulting', 'commissions',
        'credit card', 'bank charges', 'property tax', 'license',
        'permits', 'uniforms', 'training', 'recruitment', 'benefits',
        'unemployment', 'workers comp', 'health insurance', 'retirement'
      ];

      // Check for revenue keywords
      if (revenueKeywords.some(keyword => cat.includes(keyword))) {
        return 'revenue';
      }

      // Check for expense keywords
      if (expenseKeywords.some(keyword => cat.includes(keyword))) {
        return 'expense';
      }

      // Default logic: if it's a positive number and doesn't clearly indicate expense,
      // it's more likely to be revenue in hotel P&L
      return 'other';
    };

    // Create a much more robust unique key for financial items to avoid duplicates
    const createItemKey = (item: any): string => {
      // Safely handle null/undefined values
      if (!item || item.year === null || item.year === undefined) {
        console.warn('FinancialDataOverview - Item with null/undefined year:', item);
        return 'invalid-item';
      }

      // Normalize the category name to catch variations
      const normalizedCategory = (item.category || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');
      const itemName = (item.item_name || item.itemName || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');
      const value = safeParseNumber(item.value);
      const year = String(item.year); // Safe conversion to string

      // Create a comprehensive key that includes normalized values to catch exact duplicates
      // Include source to distinguish between different data sources if needed
      const source = item.source || 'unknown';

      // Round values to nearest dollar to catch minor rounding differences
      const roundedValue = Math.round(value);

      // Create multiple variations of the key to catch different field name conventions
      const baseKey = `${year}-${normalizedCategory}-${roundedValue}`;
      const itemKey = itemName ? `${year}-${normalizedCategory}-${itemName}-${roundedValue}` : baseKey;

      return itemKey;
    };

    // Group data by year
    const yearlyData: Record<string, any> = {};
    const availableYears = new Set<number>();
    const processedItems = new Set<string>(); // Track processed items to avoid duplicates
    const itemDetails = new Map<string, any>(); // Store item details for debugging

    // Process confirmed financials FIRST (highest priority)
    confirmedFinancials.forEach((item: any) => {
      // Skip items with invalid/null year
      if (!item || item.year === null || item.year === undefined) {
        console.warn('FinancialDataOverview - Skipping confirmed financial item with null year:', item);
        return;
      }

      const year = String(item.year);
      availableYears.add(Number(item.year));
      const itemKey = createItemKey(item);

      // Store details for debugging
      itemDetails.set(itemKey, {
        ...item,
        source: 'confirmed',
        processedKey: itemKey
      });

      if (!yearlyData[year]) {
        yearlyData[year] = {};
      }

      const value = safeParseNumber(item.value);
      const categoryType = categorizeFinancialItem(item.category);

      console.log(`Processing confirmed financial - Year: ${year}, Category: ${item.category}, Type: ${categoryType}, Value: ${value}, Key: ${itemKey}`);

      // Special handling for revenue to prevent intra-table duplicates
      if (categoryType === 'revenue') {
        // Check if we've already processed a similar revenue amount for this year
        const existingRevenue = yearlyData[year].revenue || 0;

        // If the new value is exactly the same as what we already have,
        // it's likely a duplicate (e.g., "Revenue" $100k and "Rooms Revenue" $100k)
        if (existingRevenue > 0 && Math.abs(value - existingRevenue) < 1.0 && value > 1000) {
          console.log(`⚠️ Skipping potential duplicate revenue within confirmed financials: ${year} ${item.category} = $${value.toLocaleString()} (already have $${existingRevenue.toLocaleString()})`);

          // Mark this item as processed but don't add to revenue
          processedItems.add(itemKey);
          return;
        }

        // Also check if this amount would create suspiciously high revenue
        // (e.g., if we already have $100k and adding another $100k would be unlikely)
        const potentialTotal = existingRevenue + value;
        if (existingRevenue > 50000 && value > 50000 && potentialTotal > existingRevenue * 1.8) {
          console.log(`⚠️ Skipping potentially doubled revenue: ${year} ${item.category} = $${value.toLocaleString()} (would create total of $${potentialTotal.toLocaleString()})`);

          // Mark this item as processed but don't add to revenue
          processedItems.add(itemKey);
          return;
        }

        yearlyData[year].revenue = (yearlyData[year].revenue || 0) + value;
      } else if (categoryType === 'expense') {
        yearlyData[year].expenses = (yearlyData[year].expenses || 0) + value;
      } else {
        // For 'other' items, make a reasonable assumption based on common hotel P&L structure
        // Most non-revenue items in hotel P&L are expenses
        yearlyData[year].expenses = (yearlyData[year].expenses || 0) + value;
      }

      // Store individual items for detailed view
      if (!yearlyData[year].details) yearlyData[year].details = [];
      yearlyData[year].details.push({
        category: item.category,
        value: value,
        itemName: item.item_name,
        type: categoryType,
        source: 'confirmed'
      });

      // Mark this item as processed
      processedItems.add(itemKey);
    });

    // Process profit & loss data ONLY if not already processed from confirmed financials
    // Be more conservative if we already have confirmed financials
    profitLoss.forEach((item: any) => {
      const itemKey = createItemKey(item);

      // Check if this exact item was already processed
      if (processedItems.has(itemKey)) {
        console.log(`Skipping duplicate P&L item: ${itemKey} - already processed from confirmed financials`);
        return;
      }

      // Also check for similar items (different variations of the same data)
      const similarKeys = Array.from(processedItems).filter(existingKey => {
        const existingDetails = itemDetails.get(existingKey);
        if (!existingDetails) return false;

        // Check if same year, similar category, and similar value (within 1% tolerance)
        const sameYear = existingDetails.year === item.year;
        const similarCategory = existingDetails.category.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                               item.category.toLowerCase().replace(/[^a-z0-9]/g, '');
        const value1 = safeParseNumber(existingDetails.value);
        const value2 = safeParseNumber(item.value);
        const similarValue = value1 > 0 && value2 > 0 && Math.abs(value1 - value2) / Math.max(value1, value2) < 0.01;

        return sameYear && similarCategory && similarValue;
      });

      if (similarKeys.length > 0) {
        console.log(`Skipping similar P&L item: ${itemKey} - similar to existing confirmed items: ${similarKeys.join(', ')}`);
        return;
      }

      // Skip items with invalid/null year
      if (!item || item.year === null || item.year === undefined) {
        console.warn('FinancialDataOverview - Skipping P&L item with null year:', item);
        return;
      }

      const year = String(item.year);
      const value = safeParseNumber(item.value);
      const categoryType = categorizeFinancialItem(item.category);

      // If we have confirmed financials, be more conservative about adding P&L revenue
      // Only add P&L revenue if the year doesn't have significant confirmed revenue already
      if (hasConfirmedFinancials && categoryType === 'revenue') {
        const existingRevenue = yearlyData[year]?.revenue || 0;

        // If we already have substantial revenue for this year, skip P&L revenue to avoid doubling
        if (existingRevenue > 10000) { // $10k threshold
          console.log(`Skipping P&L revenue for year ${year} - already have confirmed revenue: $${existingRevenue.toLocaleString()}`);
          return;
        }
      }

      availableYears.add(Number(item.year));

      if (!yearlyData[year]) {
        yearlyData[year] = {};
      }

      if (categoryType === 'revenue') {
        yearlyData[year].revenue = (yearlyData[year].revenue || 0) + value;
      } else if (categoryType === 'expense') {
        yearlyData[year].expenses = (yearlyData[year].expenses || 0) + value;
      } else {
        // For 'other' items, make a reasonable assumption based on common hotel P&L structure
        // Most non-revenue items in hotel P&L are expenses
        yearlyData[year].expenses = (yearlyData[year].expenses || 0) + value;
      }

      // Store individual items for detailed view
      if (!yearlyData[year].details) yearlyData[year].details = [];
      yearlyData[year].details.push({
        category: item.category,
        value: value,
        itemName: item.item_name || item.itemName,
        type: categoryType,
        source: item.source || 'legacy'
      });

      // Mark this item as processed
      processedItems.add(itemKey);

      // Store details for future similarity checks
      itemDetails.set(itemKey, {
        ...item,
        source: item.source || 'legacy',
        processedKey: itemKey
      });
    });

    // Process hotel metrics
    hotelMetrics.forEach((metric: any) => {
      // Skip metrics with invalid/null year
      if (!metric || metric.year === null || metric.year === undefined) {
        console.warn('FinancialDataOverview - Skipping hotel metric with null year:', metric);
        return;
      }

      const year = String(metric.year);
      availableYears.add(Number(metric.year));

      if (!yearlyData[year]) {
        yearlyData[year] = {};
      }

      const type = metric.type.toLowerCase();
      const value = safeParseNumber(metric.value);

      if (type.includes('occupancy')) {
        yearlyData[year].occupancyRate = value / 100; // Convert to decimal
      } else if (type.includes('adr') || type.includes('daily rate')) {
        yearlyData[year].adr = value;
      } else if (type.includes('revpar')) {
        yearlyData[year].revPAR = value;
      }
    });

    // Debug logging
    console.log('FinancialDataOverview - Processed financial items:', {
      confirmedFinancials: confirmedFinancials.length,
      profitLoss: profitLoss.length,
      processedUniqueItems: processedItems.size,
      yearlyData: Object.keys(yearlyData).map(year => ({
        year,
        revenue: yearlyData[year].revenue,
        expenses: yearlyData[year].expenses,
        detailsCount: yearlyData[year].details?.length || 0
      }))
    });

    // Calculate YoY changes and trends
    const years = Array.from(availableYears).sort((a, b) => b - a);
    const yearlyDataWithChanges: Record<string, any> = {};

    years.forEach((year, index) => {
      const currentYear = String(year);
      const previousYear = years[index + 1] ? String(years[index + 1]) : undefined;

      yearlyDataWithChanges[currentYear] = { ...yearlyData[currentYear] };

      if (previousYear && yearlyData[previousYear]) {
        const current = yearlyData[currentYear];
        const previous = yearlyData[previousYear];

        // Calculate YoY changes
        yearlyDataWithChanges[currentYear].changes = {
          revenue: calculateYoYChange(current.revenue, previous.revenue),
          expenses: calculateYoYChange(current.expenses, previous.expenses),
          noi: calculateYoYChange(
            (current.revenue || 0) - (current.expenses || 0),
            (previous.revenue || 0) - (previous.expenses || 0)
          ),
          occupancy: calculateYoYChange(current.occupancyRate, previous.occupancyRate),
          adr: calculateYoYChange(current.adr, previous.adr),
          revPAR: calculateYoYChange(current.revPAR, previous.revPAR)
        };
      }
    });

    // Calculate aggregated metrics with trends
    let totalRevenue = 0;
    let totalExpenses = 0;
    let totalOccupancy = 0;
    let totalADR = 0;
    let totalRevPAR = 0;
    let revenueCount = 0;
    let expenseCount = 0;
    let occupancyCount = 0;
    let adrCount = 0;
    let revPARCount = 0;

    // Calculate trends for summary cards
    const revenueTrend = calculateTrend(years.map(y => yearlyData[String(y)]?.revenue || 0));
    const expenseTrend = calculateTrend(years.map(y => yearlyData[String(y)]?.expenses || 0));
    const noiTrend = calculateTrend(years.map(y =>
      (yearlyData[String(y)]?.revenue || 0) - (yearlyData[String(y)]?.expenses || 0)
    ));
    const occupancyTrend = calculateTrend(years.map(y => yearlyData[String(y)]?.occupancyRate || 0));
    const adrTrend = calculateTrend(years.map(y => yearlyData[String(y)]?.adr || 0));
    const revPARTrend = calculateTrend(years.map(y => yearlyData[String(y)]?.revPAR || 0));

    Object.values(yearlyData).forEach((data: any) => {
      if (data.revenue && data.revenue > 0) {
        totalRevenue += data.revenue;
        revenueCount++;
      }
      if (data.expenses && data.expenses > 0) {
        totalExpenses += data.expenses;
        expenseCount++;
      }
      if (data.occupancyRate && data.occupancyRate > 0) {
        totalOccupancy += data.occupancyRate;
        occupancyCount++;
      }
      if (data.adr && data.adr > 0) {
        totalADR += data.adr;
        adrCount++;
      }
      if (data.revPAR && data.revPAR > 0) {
        totalRevPAR += data.revPAR;
        revPARCount++;
      }
    });

    // Calculate averages with proper NaN handling
    const avgRevenue = revenueCount > 0 ? totalRevenue / revenueCount : 0;
    const avgExpenses = expenseCount > 0 ? totalExpenses / expenseCount : 0;
    const avgOccupancy = occupancyCount > 0 ? totalOccupancy / occupancyCount : 0;
    const avgADR = adrCount > 0 ? totalADR / adrCount : 0;
    const avgRevPAR = revPARCount > 0 ? totalRevPAR / revPARCount : 0;

    return {
      yearlyData: yearlyDataWithChanges,
      summary: {
        totalRevenue: avgRevenue,
        totalExpenses: avgExpenses,
        averageOccupancy: avgOccupancy,
        averageADR: avgADR,
        averageRevPAR: avgRevPAR,
        yearsOfData: years,
        netOperatingIncome: avgRevenue - avgExpenses,
        trends: {
          revenue: revenueTrend,
          expenses: expenseTrend,
          noi: noiTrend,
          occupancy: occupancyTrend,
          adr: adrTrend,
          revPAR: revPARTrend
        }
      },
      rawData: {
        confirmedFinancials,
        hotelMetrics,
        profitLoss
      }
    };
  }, [financialData]);

  // Calculate data quality with detailed explanations
  const calculatedDataQuality = useMemo(() => {
    if (dataQuality) return dataQuality;

    const available: string[] = [];
    const missing: string[] = [];

    if (financialData?.profitLoss?.length > 0) {
      available.push('Profit & Loss Data');
    } else {
      missing.push('Profit & Loss Data');
    }

    if (financialData?.marketResearch?.length > 0) {
      available.push('Market Research');
    } else {
      missing.push('Market Research');
    }

    if (financialData?.confirmedFinancials?.length > 0) {
      available.push('Confirmed Financials');
    } else {
      missing.push('Confirmed Financials');
    }

    if (financialData?.hotelMetrics?.length > 0) {
      available.push('Hotel Metrics');
    } else {
      missing.push('Hotel Metrics');
    }

    const score = (available.length / (available.length + missing.length)) * 100;
    const level = score >= 80 ? 'high' : score >= 60 ? 'medium' : 'low';

    return { score, level, available, missing };
  }, [financialData, dataQuality]);

  // Export functionality
  const exportToCSV = () => {
    if (!processedFinancialData) return;

    const { yearlyData } = processedFinancialData;
    const headers = tableColumns.filter(col => col.visible).map(col => col.label);

    const rows = Object.entries(yearlyData)
      .sort(([a], [b]) => parseInt(b) - parseInt(a))
      .map(([year, data]: [string, any]) => {
        const row: any = {};
        if (tableColumns.find(col => col.id === 'year')?.visible) row.Year = year;
        if (tableColumns.find(col => col.id === 'revenue')?.visible)
          row.Revenue = data.revenue ? formatCurrency(data.revenue) : '-';
        if (tableColumns.find(col => col.id === 'revenueChange')?.visible)
          row['Revenue YoY'] = data.changes?.revenue ? `${data.changes.revenue.toFixed(1)}%` : '-';
        if (tableColumns.find(col => col.id === 'expenses')?.visible)
          row.Expenses = data.expenses ? formatCurrency(data.expenses) : '-';
        if (tableColumns.find(col => col.id === 'expenseChange')?.visible)
          row['Expense YoY'] = data.changes?.expenses ? `${data.changes.expenses.toFixed(1)}%` : '-';
        if (tableColumns.find(col => col.id === 'noi')?.visible)
          row.NOI = (data.revenue && data.expenses) ? formatCurrency(data.revenue - data.expenses) : '-';
        if (tableColumns.find(col => col.id === 'noiChange')?.visible)
          row['NOI YoY'] = data.changes?.noi ? `${data.changes.noi.toFixed(1)}%` : '-';
        if (tableColumns.find(col => col.id === 'occupancy')?.visible)
          row.Occupancy = data.occupancyRate ? formatPercentage(data.occupancyRate) : '-';
        if (tableColumns.find(col => col.id === 'occupancyChange')?.visible)
          row['Occupancy YoY'] = data.changes?.occupancy ? `${data.changes.occupancy.toFixed(1)}%` : '-';
        if (tableColumns.find(col => col.id === 'adr')?.visible)
          row.ADR = data.adr ? formatCurrency(data.adr) : '-';
        if (tableColumns.find(col => col.id === 'adrChange')?.visible)
          row['ADR YoY'] = data.changes?.adr ? `${data.changes.adr.toFixed(1)}%` : '-';
        if (tableColumns.find(col => col.id === 'revpar')?.visible)
          row.RevPAR = data.revPAR ? formatCurrency(data.revPAR) : '-';
        if (tableColumns.find(col => col.id === 'revparChange')?.visible)
          row['RevPAR YoY'] = data.changes?.revPAR ? `${data.changes.revPAR.toFixed(1)}%` : '-';

        return row;
      });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => Object.values(row).join(','))
    ].join('\n');

    // Download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `financial_data_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatCurrency = (value: number) => {
    if (isNaN(value) || value === null || value === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: value >= 1000000 ? 'compact' : 'standard'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    if (isNaN(value) || value === null || value === undefined) {
      return '0.0%';
    }
    return `${(value * 100).toFixed(1)}%`;
  };

  const getDataQualityColor = (level: string) => {
    switch (level) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const getDataQualityIcon = (level: string) => {
    switch (level) {
      case 'high': return <CheckCircleIcon />;
      case 'medium': return <WarningIcon />;
      case 'low': return <ErrorIcon />;
      default: return <WarningIcon />;
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'flat', color?: string) => {
    const iconColor = color || (trend === 'up' ? theme.palette.success.main :
                                trend === 'down' ? theme.palette.error.main :
                                theme.palette.grey[500]);

    switch (trend) {
      case 'up': return <TrendingUpIcon sx={{ color: iconColor }} fontSize="small" />;
      case 'down': return <TrendingDownIcon sx={{ color: iconColor }} fontSize="small" />;
      case 'flat': return <TrendingFlatIcon sx={{ color: iconColor }} fontSize="small" />;
    }
  };

  const formatYoYChange = (change: number | null) => {
    if (change === null || change === undefined) return '-';
    const color = change >= 0 ? theme.palette.success.main : theme.palette.error.main;
    const icon = change >= 0 ? '↑' : '↓';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color }}>
        <span>{icon}</span>
        <span style={{ marginLeft: 4 }}>{Math.abs(change).toFixed(1)}%</span>
      </Box>
    );
  };

  // Column toggle functionality
  const toggleColumn = (columnId: string) => {
    setTableColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // Table sorting
  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortOrder('desc');
    }
  };

  // Sort data
  const sortedYearlyData = useMemo(() => {
    if (!processedFinancialData) return [];

    const { yearlyData } = processedFinancialData;
    return Object.entries(yearlyData)
      .sort(([a, dataA], [b, dataB]) => {
        let aValue, bValue;

        switch (sortBy) {
          case 'year':
            aValue = parseInt(a);
            bValue = parseInt(b);
            break;
          case 'revenue':
            aValue = (dataA as any).revenue || 0;
            bValue = (dataB as any).revenue || 0;
            break;
          case 'expenses':
            aValue = (dataA as any).expenses || 0;
            bValue = (dataB as any).expenses || 0;
            break;
          case 'noi':
            aValue = ((dataA as any).revenue || 0) - ((dataA as any).expenses || 0);
            bValue = ((dataB as any).revenue || 0) - ((dataB as any).expenses || 0);
            break;
          case 'occupancy':
            aValue = (dataA as any).occupancyRate || 0;
            bValue = (dataB as any).occupancyRate || 0;
            break;
          case 'adr':
            aValue = (dataA as any).adr || 0;
            bValue = (dataB as any).adr || 0;
            break;
          case 'revpar':
            aValue = (dataA as any).revPAR || 0;
            bValue = (dataB as any).revPAR || 0;
            break;
          default:
            aValue = parseInt(a);
            bValue = parseInt(b);
        }

        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
  }, [processedFinancialData, sortBy, sortOrder]);

  if (!financialData) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          No financial data available. Please upload financial documents first.
        </Alert>
      </Box>
    );
  }

  if (!processedFinancialData) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to process financial data. Please check the data format.
        </Alert>
      </Box>
    );
  }

  const { summary, rawData } = processedFinancialData;

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Financial Data Overview
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review and validate the financial data that will be used for valuation calculations
      </Typography>

      {/* Enhanced Data Quality Score with Explanations */}
      <Card sx={{ mb: 3, border: `2px solid ${getDataQualityColor(calculatedDataQuality.level)}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {getDataQualityIcon(calculatedDataQuality.level)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              Data Quality: {calculatedDataQuality.level.toUpperCase()}
            </Typography>
            <Chip
              label={`${Math.round(calculatedDataQuality.score)}%`}
              color={calculatedDataQuality.level === 'high' ? 'success' : calculatedDataQuality.level === 'medium' ? 'warning' : 'error'}
              sx={{ ml: 2 }}
            />
            <Tooltip title="Click to see how data quality is calculated">
              <IconButton
                size="small"
                onClick={(e) => setDataQualityAnchorEl(e.currentTarget)}
                sx={{ ml: 1 }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <LinearProgress
            variant="determinate"
            value={calculatedDataQuality.score}
            color={calculatedDataQuality.level === 'high' ? 'success' : calculatedDataQuality.level === 'medium' ? 'warning' : 'error'}
            sx={{ mb: 2, height: 8, borderRadius: 4 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Available Data ({calculatedDataQuality.available.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculatedDataQuality.available.map((item, index) => (
                  <Chip key={index} label={item} size="small" color="success" variant="outlined" />
                ))}
              </Box>
            </Grid>

            {calculatedDataQuality.missing.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Missing Data ({calculatedDataQuality.missing.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {calculatedDataQuality.missing.map((item, index) => (
                    <Chip key={index} label={item} size="small" color="warning" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Data Quality Explanation Popover */}
      <Popover
        open={Boolean(dataQualityAnchorEl)}
        anchorEl={dataQualityAnchorEl}
        onClose={() => setDataQualityAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Data Quality Calculation
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Quality score is based on availability of key data types:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ ml: 2 }}>
            <li>Profit & Loss statements</li>
            <li>Market research reports</li>
            <li>Confirmed financial data</li>
            <li>Hotel operational metrics</li>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>High:</strong> 80%+ data available<br />
            <strong>Medium:</strong> 60-79% data available<br />
            <strong>Low:</strong> Below 60% data available
          </Typography>
        </Box>
      </Popover>

      {/* Year Range Selection with Better UX */}
      <Card sx={{ mb: 3, border: `2px solid ${theme.palette.primary.main}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AnalyticsIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Analysis Period
            </Typography>
            <Chip
              label="Affects Valuation"
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the date range for financial analysis. Changing this period will automatically recalculate your valuation.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Year"
              type="number"
              value={yearRange.start}
              onChange={(e) => onYearRangeChange({ ...yearRange, start: parseInt(e.target.value) })}
              size="small"
              sx={{ width: 120 }}
              inputProps={{ min: 2015, max: 2030 }}
            />
            <Typography>to</Typography>
            <TextField
              label="End Year"
              type="number"
              value={yearRange.end}
              onChange={(e) => onYearRangeChange({ ...yearRange, end: parseInt(e.target.value) })}
              size="small"
              sx={{ width: 120 }}
              inputProps={{ min: 2015, max: 2030 }}
            />
            {summary.yearsOfData.length > 0 && (
              <Box sx={{ ml: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Available years: {summary.yearsOfData.join(', ')}
                </Typography>
                <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                  Currently analyzing: {yearRange.start} - {yearRange.end}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Enhanced Key Financial Metrics with YoY and Trends */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Avg Annual Revenue
                </Typography>
                {getTrendIcon(summary.trends.revenue, theme.palette.success.main)}
              </Box>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {formatCurrency(summary.totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.yearsOfData.length} years of data • {summary.trends.revenue} trend
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Avg Annual Expenses
                </Typography>
                {getTrendIcon(summary.trends.expenses, theme.palette.error.main)}
              </Box>
              <Typography variant="h5" fontWeight={700} color="error.main">
                {formatCurrency(summary.totalExpenses)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Operating expenses • {summary.trends.expenses} trend
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Net Operating Income
                </Typography>
                {getTrendIcon(summary.trends.noi, theme.palette.primary.main)}
              </Box>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {formatCurrency(summary.netOperatingIncome)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Revenue - Expenses • {summary.trends.noi} trend
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {summary.averageOccupancy > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Avg Occupancy Rate
                  </Typography>
                  {getTrendIcon(summary.trends.occupancy)}
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {formatPercentage(summary.averageOccupancy)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Average annual • {summary.trends.occupancy} trend
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {summary.averageADR > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Average Daily Rate
                  </Typography>
                  {getTrendIcon(summary.trends.adr)}
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(summary.averageADR)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Per occupied room • {summary.trends.adr} trend
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {summary.averageRevPAR > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Average RevPAR
                  </Typography>
                  {getTrendIcon(summary.trends.revPAR)}
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(summary.averageRevPAR)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Revenue per available room • {summary.trends.revPAR} trend
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Enhanced Yearly Financial Performance Table */}
      {sortedYearlyData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Yearly Financial Performance
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<FilterListIcon />}
                  variant="outlined"
                  size="small"
                  onClick={(e) => setColumnMenuAnchorEl(e.currentTarget)}
                >
                  Columns
                </Button>
                <Button
                  startIcon={<FileDownloadIcon />}
                  variant="outlined"
                  size="small"
                  onClick={exportToCSV}
                >
                  Export CSV
                </Button>
              </Stack>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {tableColumns.filter(col => col.visible).map((column) => (
                      <TableCell key={column.id} align={column.id === 'year' ? 'left' : 'right'}>
                        {column.sortable ? (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            <strong>{column.label}</strong>
                          </TableSortLabel>
                        ) : (
                          <strong>{column.label}</strong>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedYearlyData.map(([year, data]: [string, any]) => (
                    <TableRow key={year} hover>
                      {tableColumns.find(col => col.id === 'year')?.visible && (
                        <TableCell component="th" scope="row">
                          <strong>{year}</strong>
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'revenue')?.visible && (
                        <TableCell align="right">
                          {data.revenue ? formatCurrency(data.revenue) : '-'}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'revenueChange')?.visible && (
                        <TableCell align="right">
                          {formatYoYChange(data.changes?.revenue)}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'expenses')?.visible && (
                        <TableCell align="right">
                          {data.expenses ? formatCurrency(data.expenses) : '-'}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'expenseChange')?.visible && (
                        <TableCell align="right">
                          {formatYoYChange(data.changes?.expenses)}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'noi')?.visible && (
                        <TableCell align="right">
                          {data.revenue && data.expenses ? (
                            <Typography
                              color={(data.revenue - data.expenses) > 0 ? 'success.main' : 'error.main'}
                              fontWeight={600}
                            >
                              {formatCurrency(data.revenue - data.expenses)}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'noiChange')?.visible && (
                        <TableCell align="right">
                          {formatYoYChange(data.changes?.noi)}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'occupancy')?.visible && (
                        <TableCell align="right">
                          {data.occupancyRate ? formatPercentage(data.occupancyRate) : '-'}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'occupancyChange')?.visible && (
                        <TableCell align="right">
                          {formatYoYChange(data.changes?.occupancy)}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'adr')?.visible && (
                        <TableCell align="right">
                          {data.adr ? formatCurrency(data.adr) : '-'}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'adrChange')?.visible && (
                        <TableCell align="right">
                          {formatYoYChange(data.changes?.adr)}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'revpar')?.visible && (
                        <TableCell align="right">
                          {data.revPAR ? formatCurrency(data.revPAR) : '-'}
                        </TableCell>
                      )}
                      {tableColumns.find(col => col.id === 'revparChange')?.visible && (
                        <TableCell align="right">
                          {formatYoYChange(data.changes?.revPAR)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchorEl}
        open={Boolean(columnMenuAnchorEl)}
        onClose={() => setColumnMenuAnchorEl(null)}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom>
            Show/Hide Columns
          </Typography>
          {tableColumns.map((column) => (
            <FormControlLabel
              key={column.id}
              control={
                <Switch
                  checked={column.visible}
                  onChange={() => toggleColumn(column.id)}
                  size="small"
                />
              }
              label={column.label}
              sx={{ display: 'block', mb: 0.5 }}
            />
          ))}
        </Box>
      </Menu>

      {/* Enhanced Data Sources Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Tooltip title="View source financial statements">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AnalyticsIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Confirmed Financials ({rawData.confirmedFinancials.length})
                  </Typography>
                  <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </Box>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
              {rawData.confirmedFinancials.length > 0 ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Year</TableCell>
                        <TableCell align="right">Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rawData.confirmedFinancials.slice(0, 10).map((item: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>{item.year}</TableCell>
                          <TableCell align="right">{formatCurrency(item.value)}</TableCell>
                        </TableRow>
                      ))}
                      {rawData.confirmedFinancials.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="text.secondary">
                              ... and {rawData.confirmedFinancials.length - 10} more records
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No confirmed financials available. Upload P&L statements to populate this data.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} md={6}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Tooltip title="View operational performance metrics">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssignmentIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Hotel Metrics ({rawData.hotelMetrics.length})
                  </Typography>
                  <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </Box>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
              {rawData.hotelMetrics.length > 0 ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric Type</TableCell>
                        <TableCell>Year</TableCell>
                        <TableCell align="right">Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rawData.hotelMetrics.slice(0, 10).map((metric: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{metric.type}</TableCell>
                          <TableCell>{metric.year}</TableCell>
                          <TableCell align="right">
                            {metric.type.toLowerCase().includes('rate') || metric.type.toLowerCase().includes('occupancy')
                              ? formatPercentage(metric.value / 100)
                              : formatCurrency(metric.value)
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                      {rawData.hotelMetrics.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="text.secondary">
                              ... and {rawData.hotelMetrics.length - 10} more records
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No hotel metrics available. Upload operational reports to populate this data.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Enhanced Data Sources Footer */}
      {financialData.documents && financialData.documents.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Data Sources ({financialData.documents.length} documents)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Financial data extracted from the following documents:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {financialData.documents.map((doc: any, index: number) => (
                <Chip
                  key={index}
                  label={`${doc.file_name || doc.document_type} (${doc.document_type})`}
                  variant="outlined"
                  size="small"
                  clickable
                  onClick={() => {
                    // Could implement document preview functionality here
                    console.log('View document:', doc);
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FinancialDataOverview;